import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { 
  IconCheck,
  IconX,
  IconAlertTriangle,
  IconKey,
  IconMail,
  IconShieldCheck
} from "@tabler/icons-react";
import AccountActivationService from "../services/AccountActivationService";
import Logo from "../images/mercadoagentes_logo.png";
import { useWhitelabel } from "../contexts/WhitelabelContext";

const AccountActivation = () => {
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm();

  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState("validating"); // validating, form, success, error
  const [errorMessage, setErrorMessage] = useState("");
  const { brandName } = useWhitelabel();

  useEffect(() => {
    // Extrair parâmetros da URL
    const searchParams = new URLSearchParams(window.location.search);
    const token = searchParams.get("token");
    const email = searchParams.get("email");

    if (!token || !email) {
      setErrorMessage("Link de ativação inválido. Token ou email não encontrados.");
      setStep("error");
      return;
    }

    // Preencher os campos do formulário
    setValue("email", email);
    setValue("token", token);
    setStep("form");
  }, [setValue]);

  const onSubmit = async (data) => {
    // Guard contra double-submit (Enter rápido ou duplo clique antes do re-render)
    if (loading) return;
    try {
      setLoading(true);
      setErrorMessage("");

      const { token, email } = data;

      await AccountActivationService.activateAccount(token, email, null);
      
      // Se chegou aqui, a ativação foi bem-sucedida
      // O serviço já faz o redirecionamento automático
      setStep("success");
    } catch (error) {
      console.error("Erro na ativação:", error);
      setErrorMessage(error.message);
      setStep("error");
    } finally {
      setLoading(false);
    }
  };

  const renderValidatingStep = () => (
    <div className="activation-step animate-slide-in">
      <div className="step-content text-center">
        <div className="loading-animation">
          <div className="spinner-modern"></div>
        </div>
        <h3 className="step-title">Validando link de ativação...</h3>
        <p className="step-description">Aguarde enquanto verificamos os dados.</p>
      </div>
    </div>
  );

  const renderFormStep = () => (
    <div className="activation-step animate-slide-in">
     

      <form onSubmit={handleSubmit(onSubmit)} className="modern-form">
        <div className="form-group">
          <label className="form-label">
            <IconMail size={18} />
            Email da conta
          </label>
          <div className="input-wrapper">
            <input
              type="email"
              placeholder="Email da conta"
              className={`modern-input ${errors.email ? 'error' : ''}`}
              {...register("email", {
                required: "Email é obrigatório",
                pattern: {
                  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                  message: "Formato de email inválido",
                },
              })}
              readOnly
            />
          </div>
          {errors.email && (
            <div className="error-message">
              <IconAlertTriangle size={16} />
              {errors.email.message}
            </div>
          )}
        </div>

        <div className="form-group">
          <label className="form-label">
            <IconKey size={18} />
            Token de ativação
          </label>
          <div className="input-wrapper">
            <input
              type="password"
              placeholder="Token de ativação"
              className={`modern-input ${errors.token ? 'error' : ''}`}
              {...register("token", {
                required: "Token é obrigatório",
              })}
              readOnly
            />
          </div>
          {errors.token && (
            <div className="error-message">
              <IconAlertTriangle size={16} />
              {errors.token.message}
            </div>
          )}
        </div>

        <button
          type="submit"
          className="btn-modern-primary"
          disabled={loading}
        >
          <span className="btn-text">
            {loading ? "Ativando conta..." : "Ativar Conta"}
          </span>
          {loading ? (
            <div className="spinner-modern small btn-icon" />
          ) : (
            <IconShieldCheck size={18} className="btn-icon" />
          )}
        </button>
      </form>
    </div>
  );

  const renderSuccessStep = () => (
    <div className="activation-step animate-slide-in">
      <div className="step-content text-center">
        <div className="success-icon">
          <IconCheck size={48} />
        </div>
        <h3 className="step-title success">Conta Ativada!</h3>
        <p className="step-description">
          Sua conta foi ativada com sucesso. Você será redirecionado para o login em instantes.
        </p>
        <div className="redirect-info">
          <div className="spinner-modern small"></div>
          <span>Redirecionando...</span>
        </div>
      </div>
    </div>
  );

  const renderErrorStep = () => (
    <div className="activation-step animate-slide-in">
      <div className="step-content text-center">
        <div className="error-icon">
          <IconX size={48} />
        </div>
        <h3 className="step-title error">Erro na Ativação</h3>
        <div className="error-alert">
          <IconAlertTriangle size={20} />
          <p>{errorMessage}</p>
        </div>
        <div className="error-actions">
          <button
            className="btn-modern-primary"
            onClick={() => window.location.href = "/login"}
          >
            Ir para Login
          </button>
          <button
            className="btn-modern-secondary"
            onClick={() => window.location.reload()}
          >
            Tentar Novamente
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <div className="modern-login-container">
        {/* Background with gradient */}
        <div className="login-background">
          <div className="gradient-overlay"></div>
          <div className="floating-shapes">
            <div className="shape shape-1"></div>
            <div className="shape shape-2"></div>
            <div className="shape shape-3"></div>
            <div className="shape shape-4"></div>
          </div>
        </div>

        {/* Main content */}
        <div className="login-content">
          <div className="container">
            <div className="row justify-content-center align-items-center min-vh-100">
              <div className="col-11 col-sm-10 col-md-8 col-lg-6 col-xl-5">
                {/* Activation Card */}
                <div className="login-card">
                  {/* Logo and Header */}
                  <div className="login-header">
                    <div className="pb-4">
                      <img
                        className="logo-img"
                        src={Logo}
                        alt="mercado agentes"
                      />
                    </div>
                    <h1 className="login-title">
                      {step === "validating" && "Ativação de Conta"}
                      {step === "form" && "Ativação de Conta"}
                      {step === "success" && "Conta Ativada!"}
                      {step === "error" && "Erro na Ativação"}
                    </h1>
                    <p className="login-subtitle">
                      {step === "validating" && "Validando suas informações..."}
                      {step === "form" &&
                        "Confirme os dados para ativar sua conta"}
                      {step === "success" &&
                        "Sua conta foi ativada com sucesso"}
                      {step === "error" && "Ocorreu um problema na ativação"}
                    </p>
                  </div>

                  {/* Content */}
                  <div className="login-forms">
                    {step === "validating" && renderValidatingStep()}
                    {step === "form" && renderFormStep()}
                    {step === "success" && renderSuccessStep()}
                    {step === "error" && renderErrorStep()}
                  </div>

                  {/* Footer */}
                  <div className="login-footer">
                    <p className="copyright-text">
                      Problemas com a ativação?{" "}
                      <a href="/login" className="link-button">
                        Voltar ao login
                      </a>
                    </p>
                    <p className="copyright-text text-primary">
                      © {new Date().getFullYear()} Desenvolvido por {brandName}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AccountActivation;